import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaFileInvoiceDollar, FaCode, FaChartLine, FaRobot, FaUserMd, FaClipboardCheck } from 'react-icons/fa';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const ServicesSection = styled.section`
  padding: 120px ${({ theme }) => theme.spacing.lg};
  background: linear-gradient(180deg, #ffffff 0%, #f0f9ff 50%, #e0f2fe 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(0, 102, 204, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(0, 168, 168, 0.05) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 80px ${({ theme }) => theme.spacing.md};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const SectionBadge = styled.div`
  display: inline-block;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  background: rgba(0, 102, 204, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid rgba(0, 102, 204, 0.2);
`;

const SectionTitle = styled.h2`
  color: #1e293b;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes['4xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  
  span {
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSizes['3xl']};
  }
`;

const SectionDescription = styled.p`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.8;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xl};
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const ServiceCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  padding: ${({ theme }) => theme.spacing.xl};
  border: 1px solid rgba(0, 102, 204, 0.1);
  transition: all ${({ theme }) => theme.transitions.base};
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.8s ease-out backwards;
  animation-delay: ${({ $index }) => $index * 0.15}s;
  box-shadow: 
    0 4px 20px rgba(0, 102, 204, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 
      0 12px 40px rgba(0, 102, 204, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 1);
    border-color: rgba(0, 102, 204, 0.2);
    background: rgba(255, 255, 255, 0.95);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.secondary} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  transition: all ${({ theme }) => theme.transitions.base};
  box-shadow: 0 8px 24px rgba(0, 102, 204, 0.25);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: ${({ theme }) => theme.borderRadius.xl};
    background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    opacity: 0;
    transition: opacity ${({ theme }) => theme.transitions.base};
    z-index: -1;
    filter: blur(10px);
  }
  
  ${ServiceCard}:hover & {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 12px 32px rgba(0, 102, 204, 0.35);
    animation: ${float} 2s ease-in-out infinite;
    
    &::after {
      opacity: 0.6;
    }
  }
`;

const ServiceTitle = styled.h3`
  color: #1e293b;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const ServiceDescription = styled.p`
  color: #64748b;
  font-size: ${({ theme }) => theme.fontSizes.base};
  line-height: 1.7;
`;

const services = [
  {
    icon: <FaFileInvoiceDollar />,
    title: 'Medical Billing Services',
    description: 'Comprehensive billing solutions that maximize revenue and minimize claim denials. Streamline your billing process with our expert team and advanced technology.'
  },
  {
    icon: <FaCode />,
    title: 'Medical Coding',
    description: 'Accurate ICD-10, CPT, and HCPCS coding by certified professionals. Ensure compliance and optimize reimbursement with our precision coding services.'
  },
  {
    icon: <FaChartLine />,
    title: 'Accounts Receivable Management',
    description: 'Improve cash flow with proactive AR management. Our dedicated team follows up on unpaid claims and reduces days in AR significantly.'
  },
  {
    icon: <FaRobot />,
    title: 'Healthcare Automation',
    description: 'Leverage cutting-edge automation technology to reduce manual tasks, improve accuracy, and accelerate your revenue cycle processes.'
  },
  {
    icon: <FaUserMd />,
    title: 'Virtual Clinical Solutions',
    description: 'Remote clinical support services that extend your capabilities without adding overhead. Expert virtual assistance for your practice.'
  },
  {
    icon: <FaClipboardCheck />,
    title: 'Claims Management',
    description: 'End-to-end claims processing and insurance verification. From submission to payment posting, we handle it all with precision and speed.'
  }
];

export const Services = () => {
  return (
    <ServicesSection id="services">
      <Container>
        <SectionHeader>
          <SectionBadge>Our Services</SectionBadge>
          <SectionTitle>
            Comprehensive <span>Revenue Cycle Solutions</span>
          </SectionTitle>
          <SectionDescription>
            We specialize in advanced revenue cycle management services that streamline every aspect of your practice operations
          </SectionDescription>
        </SectionHeader>
        
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard key={index} $index={index}>
              <IconWrapper aria-hidden="true">
                {service.icon}
              </IconWrapper>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};
